import {
    Component,
    Input,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NestedFormGroupType } from '../../../../../../../core/models/form-group-type';
import { AttachmentOperationFormModel } from '../../../models/attachment/attachment-operation-form-model';
import { AttachmentOperationFormService } from '../attachment-operation/services/attachment-operation-form/attachment-operation-form.service';
import { AttachmentFormModel } from '../../../models/attachment/attachment-form-model';
import { AttachmentFormService } from '../attachment-operation/services/attachment-form/attachment-form.service';
import { CommonModule } from '@angular/common';
import { ResourcesService } from '../../../../../../../core/services/resources/resources.service';
import { map, tap } from 'rxjs';
import { ValidationResult } from '../../../../../../../core/services/validation/models/validation-result';
import { ValidationError } from '../../../../../../../core/services/validation/models/validation-error';
import { AttachmentUploadValidationErrors } from './constants/attachment-upload-validation-errors';

@Component({
    selector: 'app-attachment-upload',
    templateUrl: './attachment-upload.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
    ],
})
export class AttachmentUploadComponent implements OnInit {
    @Input() form!: FormGroup<
        NestedFormGroupType<AttachmentOperationFormModel>
    >;

    /**
     * Holds the result of file validation, including validity status and any errors.
     */
    fileValidationResult: ValidationResult | null = null;

    /**
     * Indicates whether a file is being dragged over the drop area.
     */
    isDragOver: boolean = false;

    /**
     * Counter to keep track of drag enter and drag leave events.
     * This helps to manage nested drag events correctly.
     */
    private dragCounter = 0;

    /**
     * Maximum allowed file size for upload in bytes (10MB).
     * File sizes larger than this will be rejected during validation.
     */
    readonly MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

    /**
     * Whitelist of allowed file extensions for upload, fetched from the API. The API stores extensions without the leading dot,
     * so it must be transformed to include the dot for validation purposes.
     *
     * @example ['.pdf', '.docx', '.png']
     */
    private fileExtensionWhitelist: string[] = [];

    constructor(
        private readonly attachmentFormService: AttachmentFormService,
        private readonly attachmentOperationFormService: AttachmentOperationFormService,
        private readonly resourcesService: ResourcesService,
    ) {}

    /**
     * Generates a comma-separated string of accepted file types for the file input element.
     * This is derived from the file extension whitelist array.
     *
     * @returns A string suitable for the 'accept' attribute of a file input element.
     */
    get acceptedFileTypes(): string {
        return this.fileExtensionWhitelist.join(',');
    }

    ngOnInit(): void {
        this.loadFileExtensionWhitelist();
    }

    /**
     * Checks the file validation result to determine if the uploading of the file has failed validation.
     *
     * @returns True if the file has failed validation, false otherwise.
     */
    failedFileValidation(): boolean {
        return this.fileValidationResult?.isValid === false;
    }

    /**
     * This is the event that is triggered when a file is dragged over the upload area.
     *
     * @event event The drag over event of the file.
     */
    onDragOver(event: DragEvent) {
        event.preventDefault();
    }

    /**
     * This is the event that is triggered when a file is dragged into the upload area.
     *
     * @event event The drag enter event of the file.
     */
    onDragEnter(event: DragEvent) {
        event.preventDefault();

        this.dragCounter++;
        this.isDragOver = true;
    }

    /**
     * This is the event that is triggered when a file is dragged out of the upload area.
     *
     * @event event The drag leave event of the file.
     */
    onDragLeave(event: DragEvent) {
        event.preventDefault();

        this.dragCounter--;

        if (this.dragCounter === 0) {
            this.isDragOver = false;
        }
    }

    /**
     * This is the event that is triggered when a file is drag and dropped
     * into the upload area.
     *
     * @event event The drag and drop event of the file.
     */
    onDrop(event: DragEvent) {
        event.preventDefault();

        this.dragCounter = 0;
        this.isDragOver = false;

        if (event.dataTransfer?.files.length) {
            this.uploadFileFromFileList(event.dataTransfer.files);
        }
    }

    /**
     * This is the event that is triggered when a file is selected from the user's file system.
     *
     * @event event The file input change event.
     */
    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;

        if (input.files?.length) {
            this.uploadFileFromFileList(input.files);
        }
    }

    /**
     * Clears the current file validation result.
     */
    private clearValidationResult(): void {
        this.fileValidationResult = null;
    }

    /**
     * Handles the file upload process from a FileList, including validation and invoking the upload method.
     *
     * @param fileList The list of files to be uploaded (should contain only one file).
     */
    private uploadFileFromFileList(fileList: FileList): void {
        // Clear previous validation results
        this.clearValidationResult();

        // Validate the file list structure and content
        const validationErrors: Array<ValidationError> = [];
        this.validateFileListStructure(fileList, validationErrors);

        // Once the file list structure is validated, validate the content of the file
        if (validationErrors.length === 0) {
            const file: File = fileList[0];
            this.validateFileContent(file, validationErrors);
        }

        this.fileValidationResult = {
            isValid: validationErrors.length === 0,
            errors: validationErrors,
        };

        // Stop the upload process if validation failed
        if (this.failedFileValidation()) {
            return;
        }

        // Proceed with the file upload if validation passed
        const file: File = fileList[0];
        this.uploadFile(file);
    }

    /**
     * Uploads the file by creating an attachment form and adding it to the attachment operation form to be processed
     * once the form is submitted.
     *
     * @param file The file to be uploaded.
     */
    private uploadFile(file: File): void {
        const attachmentForm: FormGroup<
            NestedFormGroupType<AttachmentFormModel>
        > = this.attachmentFormService.getFormDefinition();

        const attachmentFormModel: Partial<AttachmentFormModel> = {
            file: file,
            filename: file.name,
        };

        attachmentForm.patchValue(attachmentFormModel);

        this.attachmentOperationFormService.addAttachment(
            this.form,
            attachmentForm,
        );
    }

    /**
     * Validates that the file list contains exactly one file.
     */
    private validateFileListStructure(
        fileList: FileList,
        validationErrors: Array<ValidationError>,
    ): void {
        if (fileList.length > 1) {
            validationErrors.push(
                AttachmentUploadValidationErrors.MULTIPLE_FILES_NOT_ALLOWED,
            );
        }
    }

    private validateFileContent(
        file: File,
        validationErrors: Array<ValidationError>,
    ): void {
        this.validateFileSize(file, validationErrors);
        this.validateFileExtension(file, validationErrors);
        this.validateUniqueFilename(file, validationErrors);
    }

    /**
     * Validates that the file size does not exceed the maximum allowed limit.
     */
    private validateFileSize(
        file: File,
        validationErrors: Array<ValidationError>,
    ): void {
        if (file.size > this.MAX_FILE_SIZE_BYTES) {
            validationErrors.push(
                AttachmentUploadValidationErrors.FILE_SIZE_EXCEEDED,
            );
        }
    }

    /**
     * Validates that the file extension is included in the whitelist of allowed extensions.
     */
    private validateFileExtension(
        file: File,
        validationErrors: Array<ValidationError>,
    ): void {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const isValidExtension = this.fileExtensionWhitelist.includes(
            `.${fileExtension}`,
        );

        if (!isValidExtension) {
            validationErrors.push(
                AttachmentUploadValidationErrors.FILE_EXTENSION_NOT_ALLOWED,
            );
        }
    }

    /**
     * Validates that the filename is unique among the already selected attachments.
     */
    private validateUniqueFilename(
        file: File,
        validationErrors: Array<ValidationError>,
    ): void {
        const attachments = this.attachmentOperationFormService.getAttachments(
            this.form,
        );
        const isDuplicate = attachments.controls.some(
            (attachment) => attachment.controls.filename.value === file.name,
        );

        if (isDuplicate) {
            validationErrors.push(
                AttachmentUploadValidationErrors.DUPLICATE_FILE_NAME(file.name),
            );
        }
    }

    /**
     * Loads the whitelist of allowed file extensions from the API.
     */
    private loadFileExtensionWhitelist(): void {
        this.resourcesService
            .getFileExtensionWhitelist()
            .pipe(
                // Transforms the received whitelist to ensure all extensions are in lowercase and prefixed with a dot.
                map((whitelist: string[]) => {
                    // Ensure extensions are in the format ".pdf"
                    return whitelist.map((ext) => `.${ext.toLowerCase()}`);
                }),

                // Stores the processed whitelist in the component property for later validation use.
                tap((whitelist: string[]) => {
                    this.fileExtensionWhitelist = whitelist;
                }),
            )
            .subscribe();
    }
}
